/**
 * Display names for connector providers.
 *
 * Shared because web already had two copies of this table and they had drifted:
 * `components/connectors/api.ts` carried `openapi` / `whatsapp`, while
 * `useConnectorOAuthResults.ts` carried `mcp` and neither of those. So
 * connecting an OpenAPI connector named it on one surface and fell through to a
 * generic "Connector added." on the other. This module is the union of both, and
 * exists before native adds a third copy rather than after.
 */
export const PROVIDER_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  link: "Link",
  google: "Google",
  microsoft: "Outlook",
  zoom: "Zoom",
  slack: "Slack",
  teams: "Microsoft Teams",
  gdrive: "Google Drive",
  openapi: "OpenAPI",
  mcp: "MCP server",
  whatsapp: "WhatsApp",
  imap: "IMAP",
  apple: "Apple",
};

/**
 * Undefined for the admin-registered long tail, which has no first-party name to
 * fall back on. Callers should prefer the connector's own `name` when they have
 * one — a user should never be shown a raw provider id like `openapi`.
 */
export const providerDisplayName = (provider: string): string | undefined =>
  PROVIDER_DISPLAY_NAMES[provider];

// Names parallel the product-icon maps in web's providerIcons so a row's title
// always matches its icon.
export const CALENDAR_PROVIDER_NAMES: Readonly<Record<string, string>> = {
  google: "Google Calendar",
  microsoft: "Outlook Calendar",
};

export const MAIL_PROVIDER_NAMES: Readonly<Record<string, string>> = {
  google: "Gmail",
  microsoft: "Outlook",
  // Generic on purpose: the row's subtitle carries the address, which names the ISP.
  imap: "Email inbox (IMAP)",
};

/**
 * Connector name matching the icon chosen by web's getConnectorIcon: mail
 * product for email inboxes, calendar product for other non-login
 * Google/Microsoft connectors, vendor brand otherwise. Null when the provider
 * is unknown.
 */
export function getConnectorTitle(
  provider: string,
  isLoginConnector: boolean,
  isEmailInbox: boolean,
): string | null {
  return (
    (!isLoginConnector && isEmailInbox && MAIL_PROVIDER_NAMES[provider]) ||
    (!isLoginConnector && CALENDAR_PROVIDER_NAMES[provider]) ||
    PROVIDER_DISPLAY_NAMES[provider] ||
    null
  );
}

/**
 * Providers whose display name is a *kind*, not a product: every
 * admin-registered connector shares one of these, so "MCP server" identifies
 * none of them. Their `displayName` carries the real name ("Granola"), which is
 * why {@link connectorRowLabels} leads with it for these and only these.
 */
const LONG_TAIL_PROVIDERS: ReadonlySet<string> = new Set(["mcp", "openapi"]);

/**
 * The two lines naming a connected connector, for account rows and gallery
 * cards on both clients.
 *
 * First-party connectors lead with the product ("Google Calendar") over the
 * account identity ("you@gmail.com"). The admin-registered long tail inverts
 * that: it leads with the connector's own name ("Granola") over its kind
 * ("MCP server"), because the kind is shared by dozens of rows and names none
 * of them.
 *
 * `subtitle` is empty when a second line would only repeat the first.
 */
export function connectorRowLabels(connector: {
  provider: string;
  displayName: string;
  email: string | null;
  isLogin: boolean;
  supportsEmail: boolean;
}): { title: string; subtitle: string } {
  const isEmailInbox = !connector.isLogin && connector.supportsEmail;
  const productName = getConnectorTitle(connector.provider, connector.isLogin, isEmailInbox);

  if (LONG_TAIL_PROVIDERS.has(connector.provider)) {
    // Falls back to the kind if an unnamed connector ever reaches a client, so
    // the row can never render a blank title.
    const title = connector.displayName || (productName ?? connector.provider);
    return { title, subtitle: title === productName ? "" : (productName ?? "") };
  }

  const title = productName ?? connector.displayName;
  return {
    title,
    subtitle: connector.email ?? (title === connector.displayName ? "" : connector.displayName),
  };
}
